import React, { useState, useEffect } from 'react';
import { useNavigationBlocker } from '../contexts/NavigationBlockerContext';
import ThemeToggle from './ThemeToggle';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
// Separator available but not currently used
// import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Menu,
  LayoutDashboard,
  Package,
  ShoppingCart,
  Truck,
  Warehouse,
  FileText,
  BarChart3,
  Users,
  Bell,
  UserCircle,
  Settings,
  ChevronLeft,
  ChevronRight,
  Bot,
  LogOut,
  History,
  Monitor,
  Factory,
  Layers,
  Boxes,
  Calculator,
  FileSpreadsheet,
  ExternalLink,
} from 'lucide-react';
import authService from '../services/authService';

// Constants for reference (widths are now in Tailwind classes)
// const drawerWidth = 280;
// const collapsedDrawerWidth = 64;

const Layout = ({ children, title, activeTab }) => {
  const { navigate } = useNavigationBlocker();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Initialize sidebar state from localStorage with fallback to false
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebarCollapsed');
      return saved !== null ? JSON.parse(saved) : false;
    } catch (error) {
      console.warn('Error reading sidebar state from localStorage:', error);
      return false;
    }
  });
  const [userPermissions, setUserPermissions] = useState([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  const toggleSidebarCollapse = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);

    // Persist state to localStorage
    try {
      localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
    } catch (error) {
      console.warn('Error saving sidebar state to localStorage:', error);
    }
  };

  // Load user permissions on mount (SECURITY FIX)
  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const permissionData = await authService.validateUserPermissions();
        if (permissionData.isValid) {
          setUserPermissions(permissionData.permissions);
        } else {
          // Redirect to login if validation fails
          navigate('/login');
          return;
        }
      } catch (error) {
        console.error('Failed to load permissions:', error);
        navigate('/login');
        return;
      } finally {
        setPermissionsLoaded(true);
      }
    };

    loadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount, navigate excluded to prevent re-runs

  const handleAccountMenuSelect = async (option) => {
    if (option === 'settings') {
      navigate('/settings');
    } else if (option === 'logout') {
      try {
        await authService.logout();
      } catch (err) {
        console.error('Logout error:', err);
      }
      navigate('/login');
    }
  };

  // Icon mapping for menu items
  const iconMap = {
    dashboard: LayoutDashboard,
    products: Package,
    inventory: Package,
    warehouse: Warehouse,
    sales: ShoppingCart,
    quotations: FileText,
    'price-history': History,
    reports: BarChart3,
    purchases: Truck,
    settlements: FileText,
    materials: Boxes,
    bom: Layers,
    production: Factory,
    subcontract: ExternalLink,
    'ai-assistant': Bot,
    'cnc-monitor': Monitor,
    'bar-stock-quotes': FileSpreadsheet,
    'tool-cost-calculator': Calculator,
    users: Users,
    settings: Settings,
  };

  // Generate menu items based on server-validated permissions (SECURITY FIX)
  const getMenuItems = () => {
    if (!permissionsLoaded) {
      return []; // Don't show menu until permissions are loaded
    }

    // Get user role from auth service
    const user = authService.getCurrentUser();
    const userRole = user?.role || '';

    const baseMenuItems = [
      { key: 'dashboard', label: '仪表盘', path: '/dashboard', permission: 'reports.read', roles: ['admin', 'warehouse_manager', 'sales_person', 'engineer', 'accounting'] },
      { key: 'products', label: '产品管理', path: '/products', permission: 'products.read', roles: ['admin', 'warehouse_manager', 'sales_person', 'engineer'] },
      { key: 'inventory', label: '库存管理', path: '/inventory', permission: 'inventory.read', roles: ['admin', 'warehouse_manager', 'sales_person', 'engineer'] },
      { key: 'warehouse', label: '仓库管理', path: '/warehouse', permission: 'inventory.read', roles: ['admin', 'warehouse_manager', 'sales_person', 'engineer', 'accounting'] },
      { key: 'sales', label: '销售管理', path: '/sales', permission: 'sales.read', roles: ['admin', 'sales_person', 'warehouse_manager', 'engineer', 'accounting'] },
      { key: 'quotations', label: '报价管理', path: '/quotations', permission: 'quotations.read', roles: ['admin', 'sales_person', 'engineer', 'accounting'] },
      { key: 'price-history', label: '价格历史', path: '/price-history', permission: 'products.read', roles: ['admin', 'warehouse_manager', 'sales_person', 'accounting'] },
      { key: 'reports', label: '报表统计', path: '/reports', permission: 'reports.read', roles: ['admin', 'warehouse_manager', 'sales_person', 'accounting'] },
      { key: 'purchases', label: '采购管理', path: '/purchases', permission: 'purchase.read', roles: ['admin', 'warehouse_manager', 'sales_person', 'engineer', 'accounting'] },
    { key: 'settlements', label: '采购结算', path: '/settlements', roles: ['admin', 'accounting'] },
      { key: 'materials', label: '物料管理', path: '/materials', permission: 'materials.write', roles: ['admin', 'warehouse_manager', 'engineer'] },
      { key: 'bom', label: '物料清单', path: '/bom', permission: 'bom.write', roles: ['admin', 'warehouse_manager', 'engineer'] },
      { key: 'production', label: '生产管理', path: '/production', permission: 'production.write', roles: ['admin', 'warehouse_manager', 'sales_person', 'engineer'] },
      { key: 'subcontract', label: '外协管理', path: '/subcontract', permission: 'production.write', roles: ['admin', 'warehouse_manager', 'sales_person', 'engineer'] },
      { key: 'ai-assistant', label: 'AI 助手', path: '/agent-chat', roles: ['admin', 'warehouse_manager', 'sales_person', 'engineer', 'accounting'] },
      { key: 'bar-stock-quotes', label: '棒料报价', path: '/bar-stock-quotes', permission: 'purchase.read', roles: ['admin', 'engineer', 'accounting', 'sales_person'] },
      { key: 'tool-cost-calculator', label: '刀具成本计算器', path: '/tool-cost-calculator', roles: ['admin', 'engineer', 'accounting', 'sales_person'] }
    ];

    const adminMenuItems = [
      { key: 'users', label: '用户管理', path: '/users', permission: 'users.read', roles: ['admin'] },
      { key: 'settings', label: '系统设置', path: '/settings', roles: ['admin', 'warehouse_manager', 'sales_person', 'engineer', 'accounting'] },
    ];

    const allMenuItems = [...baseMenuItems, ...adminMenuItems];

    // Filter menu items based on user permissions and roles
    return allMenuItems.filter(item => {
      // If item has roles defined, check if user role matches
      if (item.roles && item.roles.length > 0) {
        if (!item.roles.includes(userRole)) {
          return false;
        }
      }
      
      // If item has permission, also check permission
      if (item.permission) {
        return authService.hasPermission(item.permission, userPermissions) ||
               userPermissions.includes(item.permission);
      }
      
      return true;
    });
  };

  const menuItems = getMenuItems();

  const handleMenuClick = (item) => {
    if (item.path) {
      if (item.external) {
        window.open(item.path, '_blank', 'noopener,noreferrer');
      } else {
        navigate(item.path);
      }
      setDrawerOpen(false);
    }
  };

  const NavItem = ({ item, collapsed }) => {
    const Icon = iconMap[item.key] || LayoutDashboard;
    const isActive = activeTab === item.key;

    const button = (
      <button
        onClick={() => handleMenuClick(item)}
        className={cn(
          "flex items-center w-full rounded-lg transition-all duration-200",
          collapsed ? "justify-center p-3" : "px-3 py-2.5 gap-3",
          isActive
            ? "bg-primary text-primary-foreground"
            : "hover:bg-accent text-foreground"
        )}
      >
        <Icon className={cn("h-5 w-5 shrink-0", isActive ? "text-primary-foreground" : "text-primary")} />
        {!collapsed && (
          <span className={cn("text-sm", isActive ? "font-semibold" : "font-medium")}>
            {item.label}
          </span>
        )}
      </button>
    );

    if (collapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            {button}
          </TooltipTrigger>
          <TooltipContent side="right" className="font-medium">
            {item.label}
          </TooltipContent>
        </Tooltip>
      );
    }

    return button;
  };

  const SidebarContent = ({ collapsed, isMobile = false }) => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className={cn(
        "flex items-center h-16 px-4 border-b",
        collapsed ? "justify-center" : "justify-between"
      )}>
        {!collapsed && (
          <h1 className="text-lg font-bold text-primary truncate">
            丰旗工具管理系统
          </h1>
        )}
        {!isMobile && (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleSidebarCollapse}
                className="h-8 w-8 shrink-0"
              >
                {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {collapsed ? "展开侧边栏" : "收起侧边栏"}
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <nav className={cn("space-y-1", collapsed ? "px-2" : "px-3")}>
          {!permissionsLoaded ? (
            <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
              正在加载...
            </div>
          ) : (
            menuItems.map((item) => (
              <NavItem key={item.key} item={item} collapsed={collapsed} />
            ))
          )}
        </nav>
      </ScrollArea>
    </div>
  );

  return (
    <TooltipProvider>
      <div className="flex min-h-screen bg-background w-full">
        {/* Desktop Sidebar */}
        <aside
          className={cn(
            "hidden sm:flex flex-col bg-card border-r fixed inset-y-0 left-0 z-30 transition-all duration-300",
            sidebarCollapsed ? "w-16" : "w-[280px]"
          )}
        >
          <SidebarContent collapsed={sidebarCollapsed} />
        </aside>

        {/* Mobile Drawer */}
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetContent side="left" className="p-0 w-[280px]">
            <SheetHeader className="sr-only">
              <SheetTitle>导航菜单</SheetTitle>
            </SheetHeader>
            <SidebarContent collapsed={false} isMobile={true} />
          </SheetContent>
        </Sheet>

        {/* Main Content Area */}
        <div
          className={cn(
            "flex-1 flex flex-col transition-all duration-300",
            sidebarCollapsed ? "sm:ml-16" : "sm:ml-[280px]"
          )}
        >
          {/* App Bar */}
          <header className="sticky top-0 z-20 flex items-center h-16 px-4 bg-card border-b gap-4">
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="sm:hidden"
              onClick={() => setDrawerOpen(true)}
            >
              <Menu className="h-5 w-5" />
              <span className="sr-only">打开菜单</span>
            </Button>

            {/* Page Title */}
            <h1 className="text-lg font-semibold text-foreground flex-1 truncate">
              {title}
            </h1>

            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Notifications */}
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="h-5 w-5" />
              <Badge
                variant="destructive"
                className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
              >
                4
              </Badge>
              <span className="sr-only">通知</span>
            </Button>

            {/* Account Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <UserCircle className="h-5 w-5" />
                  <span className="sr-only">用户菜单</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {permissionsLoaded && authService.hasPermission('manage_settings', userPermissions) && (
                  <DropdownMenuItem onClick={() => handleAccountMenuSelect('settings')}>
                    <Settings className="mr-2 h-4 w-4" />
                    系统设置
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => handleAccountMenuSelect('logout')}>
                  <LogOut className="mr-2 h-4 w-4" />
                  退出登录
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>

          {/* Page Content */}
          <main className="flex-1 p-6 overflow-auto">
            {children}
          </main>
        </div>

        {/* Floating AI Button */}
        {permissionsLoaded && (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                onClick={() => navigate('/agent-chat')}
                size="icon"
                className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full shadow-lg hover:scale-110 transition-transform"
              >
                <Bot className="h-7 w-7" />
                <span className="sr-only">Open AI Assistant</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              Open AI Assistant
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};

export default Layout;
